/**
 * The kit's EDITING SHAPES: the logo variation, the grouped brand colour, and the projections that turn
 * either of them back into the profile generation reads.
 *
 * Lifted out of `routes/brand-kits_.$kitId.tsx` unchanged. A route could own them while the only editor
 * was a click; it cannot once the agent edits the same kit, because an agent operation and a panel
 * operation have to produce byte-identical profiles or the two writers fight over roles and palette.
 */
import type { MoodboardProfile } from '../types/presentation'
import { normalizeHex } from './kitColorMath'

/**
 * A LOGO VARIATION — one background the brand's mark lives on, with its OWN file. "Light mode" and
 * "Dark mode" are the two built-ins; a brand can add more (colour fields, do's and don'ts).
 *
 * Stored inside `profile.logoVariations` plus `profile.activeLogoVariationId`. The backend treats the
 * profile as an opaque JSON string (verified by round-trip) and every prompt builder reads only named
 * fields, so the extra keys ride along without touching generation. The kit's own `logoUrl` column stays
 * what it always was, the file generation bakes, and it must always equal the ACTIVE variation's file.
 */
export interface LogoVariation {
  id: string
  name: string
  /** Card background, hex. Also decides `logoOnLight` when this variation is active. */
  background: string
  logoUrl?: string
}

export type ProfileWithLogoModes = MoodboardProfile & {
  logoVariations?: LogoVariation[]
  activeLogoVariationId?: string | null
}

export type ColorGroupId = 'background' | 'text' | 'accent' | 'surface' | 'card' | 'border' | 'icon' | 'other'

/**
 * A BRAND COLOR — a name ("Ocean"), a value, and a PURPOSE (its group). The Colors page is organised by
 * purpose: as many backgrounds, text colors, accents as the brand needs, grouped into sections.
 *
 * Priority is POSITION: the first color of a group is its primary, and the primaries project into the
 * legacy `roles` record (first background → roles.background, first/second text → text/textMuted) while
 * every color flattens into `palette` in group order. Generation and the logo cards' `var:` references
 * keep reading roles/palette untouched; the groups are the editing truth, the rest is projection.
 */
export interface BrandColor {
  id: string
  name: string
  value: string
  group: ColorGroupId
}

export type ProfileWithColorGroups = ProfileWithLogoModes & { colorGroups?: BrandColor[] }

export const COLOR_GROUPS: { id: ColorGroupId; label: string; singular: string }[] = [
  { id: 'background', label: 'Backgrounds', singular: 'Background' },
  { id: 'text', label: 'Text colors', singular: 'Text' },
  { id: 'accent', label: 'Accents', singular: 'Accent' },
  { id: 'surface', label: 'Surface colors', singular: 'Surface' },
  { id: 'card', label: 'Card colors', singular: 'Card' },
  { id: 'border', label: 'Border colors', singular: 'Border' },
  { id: 'icon', label: 'Icon colors', singular: 'Icon' },
  { id: 'other', label: 'Other colors', singular: 'Color' },
]

/** The file format stamped on a logo card ("SVG", "PNG"). Read from the stored URL's extension;
 *  null when the URL carries none, and the label simply stays off the card. */
export function logoFormatOf(url: string): string | null {
  const match = /\.(svg|png|jpe?g|webp|gif|ico)(?:[?#]|$)/i.exec(url)
  return match ? match[1].toUpperCase().replace('JPEG', 'JPG') : null
}

/** Build groups from a legacy roles+palette profile (old kits, and Figma import results). */
export function seedColorGroups(profile: MoodboardProfile): BrandColor[] {
  const out: BrandColor[] = []
  const roles = profile.roles ?? {}
  const push = (group: ColorGroupId, name: string, value?: string) => {
    if (value) out.push({ id: `seed-${group}-${out.length}`, name, value, group })
  }
  push('background', 'Background 1', roles.background)
  push('text', 'Text 1', roles.text)
  push('text', 'Text 2', roles.textMuted)
  push('accent', 'Accent 1', roles.accent)
  push('surface', 'Surface 1', roles.surface)
  push('border', 'Border 1', roles.border)
  const seen = new Set(out.map((c) => normalizeHex(c.value) ?? c.value))
  ;(profile.palette ?? []).forEach((value, index) => {
    const key = normalizeHex(value) ?? value
    if (seen.has(key)) return
    seen.add(key)
    out.push({ id: `seed-other-${index}`, name: `Color ${index + 1}`, value, group: 'other' })
  })
  return out
}

/** The colours a stored profile is edited as: its own groups when it has them, the legacy projection
 *  seeded in memory when it does not. Reading a kit must never write one. */
export function colorsOf(profile: MoodboardProfile | undefined): BrandColor[] {
  if (!profile) return []
  const groups = (profile as ProfileWithColorGroups).colorGroups
  return groups?.length ? groups : seedColorGroups(profile)
}

/**
 * A Figma import's named colors → the kit's grouped colors, one card per variable the file declares.
 * Kept in group order (the sections' order) with each group's own order untouched, so the first color of
 * a group is the one the file declared first: that is the primary generation leads with.
 */
export function fromFigmaColors(colors: { name: string; hex: string; group: string }[]): BrandColor[] {
  const allowed = new Set<string>(COLOR_GROUPS.map((g) => g.id))
  const mapped = colors.map((c) => ({
    id: `fig-${crypto.randomUUID()}`,
    name: c.name,
    value: c.hex,
    group: (allowed.has(c.group) ? c.group : 'other') as ColorGroupId,
  }))
  // Re-sorted here because validating an unknown group into Other can move a card out of section order.
  return COLOR_GROUPS.flatMap((g) => mapped.filter((c) => c.group === g.id))
}

/** Groups → the stored profile: groups as truth, roles + palette recomputed as projections. */
export function projectColorGroups(base: MoodboardProfile, colors: BrandColor[]): MoodboardProfile {
  const firstOf = (group: ColorGroupId) => colors.find((c) => c.group === group)?.value
  const texts = colors.filter((c) => c.group === 'text')
  const roles: Record<string, string> = {}
  const set = (key: string, value?: string) => {
    if (value) roles[key] = value
  }
  set('background', firstOf('background'))
  set('surface', firstOf('surface'))
  set('text', texts[0]?.value)
  set('textMuted', texts[1]?.value)
  set('accent', firstOf('accent'))
  set('border', firstOf('border'))
  const palette = COLOR_GROUPS.flatMap((g) => colors.filter((c) => c.group === g.id).map((c) => c.value))
  return {
    ...base,
    palette,
    roles: Object.keys(roles).length ? roles : undefined,
    colorGroups: colors,
  } as unknown as MoodboardProfile
}
